import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { FormInput, Layers, Cpu, ArrowRight } from 'lucide-react'
import { CodeDemo } from '../ui/CodeDemo'

export const InnovationSection: React.FC = () => {
  const { t } = useTranslation('homepage')

  const formDefinitionExample = `{
  "entityType": "produto",
  "complexity": "MEDIUM",
  "form_structure": {
    "tabs": [
      {
        "id": "dados-basicos",
        "label": "Dados Básicos",
        "fields": [
          {
            "name": "nome",
            "type": "TEXT",
            "required": true,
            "label": "Nome do Produto"
          },
          {
            "name": "preco",
            "type": "CURRENCY",
            "required": true,
            "label": "Preço",
            "validation": { "min": 0.01 }
          },
          {
            "name": "categoria",
            "type": "LOOKUP",
            "endpoint": "/api/categorias",
            "displayField": "nome"
          }
        ]
      }
    ],
    "actions": [
      { "type": "SAVE", "label": "Salvar" },
      { "type": "DELETE", "label": "Excluir" }
    ]
  }
}`

  const features = [
    {
      icon: FormInput,
      title: t('innovation.dynamicForms.title'),
      description: t('innovation.dynamicForms.description'),
      color: 'text-blue-600'
    },
    {
      icon: Layers,
      title: t('innovation.typeMapping.title'),
      description: t('innovation.typeMapping.description'),
      color: 'text-green-600'
    },
    {
      icon: Cpu,
      title: t('innovation.aiIntegration.title'),
      description: t('innovation.aiIntegration.description'),
      color: 'text-purple-600'
    }
  ]

  return (
    <section className="py-24 bg-white">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-heading"
          >
            {t('innovation.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="section-subheading mx-auto"
          >
            {t('innovation.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Code Demo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="flex items-center space-x-2 p-4 bg-gray-800">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="ml-4 text-sm text-gray-400 font-mono">form_definition.json</span>
              </div>
              <CodeDemo code={formDefinitionExample} language="json" />
            </div>
          </motion.div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start space-x-4 group cursor-pointer"
              >
                <div className={`p-3 rounded-xl bg-gray-50 group-hover:bg-gray-100 transition-colors ${feature.color}`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-all" />
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="pt-6"
            >
              <button className="btn-primary">
                {t('innovation.cta')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-24 p-8 bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl"
        >
          <div className="text-center">
            <div className="text-4xl font-bold text-primary-600 mb-2">80%</div>
            <div className="text-gray-700">{t('innovation.stats.frontendReduction')}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-primary-600 mb-2">15+</div>
            <div className="text-gray-700">{t('innovation.stats.fieldTypes')}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-primary-600 mb-2">3</div>
            <div className="text-gray-700">{t('innovation.stats.provenSegments')}</div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}